<template>
  <div class="prequal-container shadow">
    <div class="row">
      <div class="col-12 col-sm-auto d-none d-sm-block">
        <div class="lender-container">
          <img :src="lenderImage" :alt="lenderName" />
        </div>
      </div>
      <div class="col">
        <div class="row">
          <div class="col-auto d-sm-none">
            <div class="lender-container">
              <img :src="lenderImage" :alt="lenderName" />
            </div>
          </div>
          <div class="col">
            <div
              v-html-safe="
                isNonPrimeLending
                  ? $t(`message.credit.application.preQualification.${lenderKey}.nonPrime.subTitle`)
                  : $t(`message.credit.application.preQualification.${lenderKey}.prime.subTitle`)
              "
              class="amount-title mb-3"
            ></div>
            <div class="amount">{{ $n(loanAmount, 'currencyDollars', languageCode) }}</div>
          </div>
        </div>
        <div class="message-container">
          <div
            v-if="isNonPrimeLending"
            v-html-safe="
              $t(`message.credit.application.preQualification.${lenderKey}.nonPrime.message`, {
                amount: $n(monthlyPayment, 'currencyDollars', languageCode),
              })
            "
            class="muted-bold-text-small mt-2 mt-sm-3"
          ></div>
          <div
            v-else-if="lenderKey === PrequalFinanceProviderCodes.TD"
            v-html-safe="
              $t(`message.credit.application.preQualification.TD.prime.message`, {
                amount: $n(monthlyPayment, 'currencyDollars', languageCode),
              })
            "
            class="muted-bold-text-small mt-2 mt-sm-3"
          ></div>
        </div>
        <div v-if="!isNonPrimeLending" class="row mt-2 mt-sm-3">
          <div v-if="interestRate" class="col-12 col-sm-4">
            <div class="interest">
              <p class="finance-details-top mt-1 mt-sm-0 mb-1">
                {{ $t(`message.credit.application.preQualification.${lenderKey}.interestRate`) }}
              </p>
              <div class="finance-details-bottom">{{ interestRate }}%</div>
            </div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="payment">
              <p class="finance-details-top mt-3 mt-sm-0 mb-1">
                {{ $t(`message.credit.application.preQualification.${lenderKey}.paymentAmount`) }}
              </p>
              <div class="finance-details-bottom">
                {{ $n(monthlyPayment, 'currencyDollars', languageCode) }}/{{ $t('message.common.month') }}
              </div>
            </div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="payment-months">
              <p class="finance-details-top mt-3 mt-sm-0 mb-1">
                {{ $t(`message.credit.application.preQualification.${lenderKey}.loanTerm`) }}
              </p>
              <div class="finance-details-bottom">{{ loanTermInMonths }} {{ $t('message.common.monthPlural') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <mobialis-button
      v-if="financeApplicationEnabled"
      class="mt-4"
      @click="lenderSelected"
      v-av-analytics-cta-interaction="{ elementText: $t('message.common.applyNow'), eventActionResult: 'open' }"
    >
      {{ $t('message.common.applyNow') }}
    </mobialis-button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PrequalFinanceProviderCodes from '@/utils/enums/prequalFinanceProviderCodes';

export default {
  name: 'FinanceLenderCard',
  data() {
    return {
      lenderImage: '',
      PrequalFinanceProviderCodes,
    };
  },
  props: {
    lenderKey: {
      type: String,
      required: true,
      validator(value) {
        return [PrequalFinanceProviderCodes.TD, PrequalFinanceProviderCodes.GL].includes(value);
      },
    },
    lenderName: {
      type: String,
      required: true,
    },
    loanAmount: {
      type: [String, Number],
      required: true,
    },
    interestRate: {
      type: [String, Number],
      required: false,
    },
    monthlyPayment: {
      type: [String, Number],
      required: true,
    },
    loanTermInMonths: {
      type: [String, Number],
      required: false,
    },
  },
  components: {},
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      financeApplicationEnabled: 'business/financeApplicationEnabled',
    }),
    isNonPrimeLending() {
      // To-do this should probably come from the backend just in case this gets complex
      return this.interestRate === null && this.loanTermInMonths === null;
    },
  },
  methods: {
    lenderSelected() {
      this.$emit('lender-selected');
    },
    async getLenderImage() {
      if (this.lenderKey) {
        // Prevent non-existent image from stopping the component from rendering
        try {
          // eslint-disable-next-line
          this.lenderImage = await require(`@/assets/prequal-lenders/${this.lenderKey}-logo.svg`);
        } catch (e) {
          // Nothing for now
        }
      }
    },
  },
  mounted() {
    this.getLenderImage();
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.prequal-container {
  padding: 24px;
  border-radius: 6px;
  .lender-container {
    img {
      width: 100px;
    }
  }
  .amount-title {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    color: $primary-grey;
  }
  .amount {
    font-size: 24px;
    font-weight: 700;
    line-height: 30px;
  }
  p.finance-details-top {
    font-size: 12px;
    line-height: 18px;
  }
  .finance-details-bottom {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
  }
}

.muted-bold-text-small {
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #6d7783;
}

/* Mobile only */
@media only screen and (max-width: $breakpoint-mobile) {
  .prequal-container {
    .lender-container {
      img {
        width: 86px;
      }
    }
    .amount-container {
      .amount {
        font-size: 20px;
        line-height: 25px;
      }
      .amount-title {
        font-size: 14px;
        line-height: 18px;
      }
    }
  }
}
</style>
